/** @jsxRuntime classic */
/** @jsx jsx */

import { css, jsx } from '@emotion/react';
import { Colors, Typography } from '@zapier/design-system';
import { labelStyles, withModifier } from '@zapier/style-encapsulation';
import {
  HeadingSize,
  HEADING_SIZE_MAP,
  MEDIA_MIN_WIDTH,
} from '../../../constants';
import { RichTextHtml } from '../../RichTextHtml/RichTextHtml';
import { RichTextHTMLString } from '../types';

export interface TableSectionProps {
  title?: string;
  headers?: { text: string }[];
  rows?: {
    cells?: {
      text: RichTextHTMLString;
      rowSpan?: number;
      columnSpan?: number;
    }[];
  }[];
  sectionSpacing?: '0' | '20px' | '40px' | '60px' | '80px';
  titleElement?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  titleSize?: HeadingSize;
  titleAlignment?: 'center' | 'left';
  scrollableOnMobile?: boolean;
}

const styles = labelStyles('Table', {
  root: (sectionSpacing: TableSectionProps['sectionSpacing'] = '40px') =>
    css({
      margin: `${sectionSpacing} 0`,
    }),

  title: (
    titleSize: TableSectionProps['titleSize'] = 'medium',
    titleAlignment: TableSectionProps['titleAlignment'] = 'center',
  ) =>
    css({
      ...Typography[HEADING_SIZE_MAP[titleSize].mobile],
      maxWidth: '1170px',
      margin: '0 auto 1em',
      padding: '0 20px',
      textAlign: titleAlignment,
      color: Colors.neutral800,

      [MEDIA_MIN_WIDTH.small]: {
        ...Typography[HEADING_SIZE_MAP[titleSize].tablet],
      },

      [MEDIA_MIN_WIDTH.large]: {
        ...Typography[HEADING_SIZE_MAP[titleSize].desktop],
      },
    }),

  tableContainer: css({
    overflowX: 'auto',
    padding: '0 20px',
  }),

  table: (scrollableOnMobile = false) => [
    css({
      width: '100%',
      maxWidth: '1170px',
      margin: '0 auto',
      borderCollapse: 'collapse',
    }),

    scrollableOnMobile &&
      withModifier(
        'scrollable-on-mobile',
        css({
          width: '200%',

          [MEDIA_MIN_WIDTH.small]: {
            width: '100%',
          },
        }),
      ),
  ],

  cell: css({
    ...Typography.smallPrint1,
    minWidth: '80px',
    padding: '8px',
    boxSizing: 'border-box',
    border: `1px solid ${Colors.neutral400}`,
    verticalAlign: 'middle',
    textAlign: 'left',
    color: Colors.neutral800,

    [MEDIA_MIN_WIDTH.small]: {
      ...Typography.paragraph3,
      padding: '12px',
    },

    [MEDIA_MIN_WIDTH.large]: {
      ...Typography.paragraph1Short,
      padding: '16px',
    },
  }),

  header: css({
    ...Typography.smallPrint1Semibold,
    backgroundColor: Colors.neutral200,

    [MEDIA_MIN_WIDTH.small]: {
      ...Typography.paragraph3Semibold,
    },

    [MEDIA_MIN_WIDTH.large]: {
      ...Typography.paragraph1ShortSemibold,
    },
  }),
});

export function TableSection(props: TableSectionProps) {
  const Title = props.titleElement || 'h2';

  return (
    <div css={styles.root(props.sectionSpacing)}>
      {props.title && (
        <Title css={styles.title(props.titleSize, props.titleAlignment)}>
          {props.title}
        </Title>
      )}
      <div css={styles.tableContainer}>
        <table css={styles.table(props.scrollableOnMobile)}>
          {props.headers && props.headers.length > 0 && (
            <thead>
              <tr>
                {props.headers.map((header, index) => (
                  <th css={[styles.cell, styles.header]} key={index}>
                    {header.text}
                  </th>
                ))}
              </tr>
            </thead>
          )}
          {props.rows && props.rows.length > 0 && (
            <tbody>
              {props.rows.map((row, rowIndex) => (
                <tr css={styles.row} key={rowIndex}>
                  {row.cells?.map((cell, cellIndex) => (
                    <td
                      colSpan={cell.columnSpan}
                      css={styles.cell}
                      key={cellIndex}
                      rowSpan={cell.rowSpan}
                    >
                      <RichTextHtml html={cell.text} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </div>
    </div>
  );
}
